import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, CheckCircle2, FileText, Shield, Sparkles, ExternalLink } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { PageGallery } from "@/components/PageGallery";
import { COMPANY } from "@/lib/company";
import type { PoliceClearanceCountry } from "@/content/police-clearance/registry";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { PC_KEYWORDS_TH_TOP as PC_KEYWORDS_TH } from "@/content/police-clearance-corpus/top.gen";

function buildFaqs(c: PoliceClearanceCountry) {
  return [
    {
      q: `ค่าบริการขอ ${c.certName} จากภูเก็ตสอบถามได้ที่ไหน?`,
      a: `ค่าบริการขึ้นอยู่กับค่าธรรมเนียมของ ${c.authorityShort} เส้นทางรับรอง (Apostille/MFA) และค่าจัดส่ง จึงเสนอราคาเป็นรายกรณี ส่งรูปพาสปอร์ตและหนังสือที่หน่วยงานปลายทางขอมาที่ LINE ${COMPANY.lineId} โทร ${COMPANY.phone} หรืออีเมล ${COMPANY.email} ทีมงานยืนยันรายการเอกสาร ค่าบริการ และกรอบเวลาก่อนเริ่มงานทุกครั้ง`,
    },
    {
      q: `ใช้เวลาเท่าไหร่?`,
      a: c.turnaround,
    },
    {
      q: `ต้องบินกลับ${c.countryTh}ไหม?`,
      a: `ไม่ต้อง — ทีมเราในภูเก็ตจัดการครบ ${c.fingerprints} ทุกขั้นตอนผ่าน Power of Attorney และพาร์ทเนอร์ใน${c.countryTh}`,
    },
    {
      q: `ใบรับรองมีอายุเท่าไหร่?`,
      a: c.validity,
    },
    {
      q: `ต้องรับรอง Apostille หรือ MFA เพิ่มไหม?`,
      a: c.legalization,
    },
    {
      q: `ใช้สำหรับอะไรได้บ้าง?`,
      a: `${c.useCases.join(" · ")} — แจ้งจุดประสงค์การใช้ทางไลน์ ${COMPANY.lineId} ทีมแนะนำแพ็กที่ตรงกับวีซ่า/วัตถุประสงค์ของคุณ`,
    },
  ];
}

export const Route = createFileRoute("/police-clearance/$country")({
  loader: async ({
    params,
  }): Promise<{
    country: PoliceClearanceCountry;
    related: { slug: string; countryTh: string; certName: string; flag?: string; authorityShort: string; priceFrom: number }[];
  }> => {
    const { getPoliceClearanceCountry, POLICE_CLEARANCE_COUNTRIES } =
      await import("@/content/police-clearance/registry");
    const c = getPoliceClearanceCountry(params.country);
    if (!c) throw notFound();
    return {
      country: c,
      related: POLICE_CLEARANCE_COUNTRIES.filter((x) => x.slug !== c.slug)
        .slice(0, 6)
        .map((x) => ({
          slug: x.slug,
          countryTh: x.countryTh,
          certName: x.certName,
          flag: (x as { flag?: string }).flag,
          authorityShort: x.authorityShort,
          priceFrom: x.priceTHB.from,
        })),
    };
  },
  head: ({ params, loaderData }) => {
    const c = loaderData?.country;
    if (!c) {
      return pageHeadForProvince({
        title: "ไม่พบประเทศ",
        description: "ไม่พบบริการ Police Clearance สำหรับประเทศนี้",
        path: `/police-clearance/${params.country}`,
      });
    }
    const site = getActiveSiteUrl();
    const rawTitle = `${c.certName} จากภูเก็ต`;
    const title = rawTitle.length > 60 ? rawTitle.slice(0, 59) + "…" : rawTitle;
    const rawDesc = `ขอ ${c.certName} ${c.authorityShort} สำหรับ${c.countryTh} จากภูเก็ต ไม่ต้องบินกลับ ${c.turnaround.split("·")[0].trim()} สอบถามค่าบริการทาง LINE`;
    const desc = rawDesc.length > 158 ? rawDesc.slice(0, 157) + "…" : rawDesc;
    const base = pageHeadForProvince({
      title,
      description: desc,
      path: `/police-clearance/${c.slug}`,
      alternates: [{ lang: "en", path: `/en/police-clearance/${c.slug}` }],
      keywords: [
        c.certName,
        `${c.certName} ${c.countryTh}`,
        `${c.authorityShort} ภูเก็ต`,
        `ขอ ${c.certName} จากไทย`,
        ...PC_KEYWORDS_TH.slice(0, 40),
      ],
    });
    const faqs = buildFaqs(c);
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "Police Clearance", url: `${site}/police-clearance` },
              { name: c.countryTh, url: `${site}/police-clearance/${c.slug}` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": `${site}/police-clearance/${c.slug}#service`,
            name: `${c.certName} from Phuket`,
            serviceType: "Police Clearance Certificate Procurement",
            description: c.aeoAnswer,
            provider: {
              "@type": "LegalService",
              name: COMPANY.legalName,
              telephone: COMPANY.phone,
              url: site,
            },
            areaServed: { "@type": "Country", name: c.countryEn },
            isRelatedTo: {
              "@type": "GovernmentOrganization",
              name: c.authorityFull,
              url: c.authorityUrl,
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: `วิธีขอ ${c.certName} จากภูเก็ต`,
            description: c.intro,
            totalTime: "P14D",
            step: c.steps.map((s, i) => ({
              "@type": "HowToStep",
              position: i + 1,
              name: s.title,
              text: s.body,
            })),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/police-clearance/${params.country}`)) },
      ],
    };
  },
  component: PoliceClearanceCountryPage,
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold">ไม่พบประเทศนี้</h1>
        <Link to="/police-clearance" className="mt-4 inline-flex items-center gap-1 text-gold">
          <ArrowLeft className="h-4 w-4" /> กลับรายการประเทศ
        </Link>
      </div>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="p-10 text-center">
      <h1 className="text-xl font-bold">เกิดข้อผิดพลาด</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
    </div>
  ),
});

function PoliceClearanceCountryPage() {
  const data = Route.useLoaderData() as {
    country: PoliceClearanceCountry;
    related: { slug: string; countryTh: string; certName: string; flag?: string; authorityShort: string; priceFrom: number }[];
  };
  const c = data.country;
  const faqs = buildFaqs(c);
  const related = data.related;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <nav className="border-b border-border bg-muted/30">
          <div className="mx-auto max-w-5xl px-4 py-3 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link>
            <span className="mx-2">/</span>
            <Link to="/police-clearance" className="hover:text-primary">Police Clearance</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">{c.countryTh}</span>
          </div>
        </nav>

        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <div className="text-xs font-bold uppercase tracking-widest text-gold">
              <Shield className="mr-1 inline h-3.5 w-3.5" />
              {c.flag} {c.authorityShort} · {c.countryEn}
            </div>
            <h1 className="mt-2 text-3xl font-extrabold md:text-4xl">{c.certName} จากภูเก็ต</h1>
            <p id="ai-answer-tldr" className="mt-4 rounded-2xl border border-gold/30 bg-gold/5 p-5 text-base leading-relaxed">
              <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-gold">
                <Sparkles className="mr-1 inline h-3.5 w-3.5" /> AI Quick Answer
              </span>
              {c.aeoAnswer}
            </p>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">{c.intro}</p>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto grid max-w-5xl gap-3 px-4 py-8 md:grid-cols-3">
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-semibold uppercase text-muted-foreground">ราคา</div>
              <div className="mt-1 text-lg font-extrabold text-gold">
                ฿{c.priceTHB.from.toLocaleString()} – ฿{c.priceTHB.to.toLocaleString()}
              </div>
              <div className="text-xs text-muted-foreground">แพ็กครบ จากภูเก็ต</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-semibold uppercase text-muted-foreground">ระยะเวลา</div>
              <div className="mt-1 text-sm font-bold">{c.turnaround}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-semibold uppercase text-muted-foreground">หน่วยงานออกใบ</div>
              <div className="mt-1 text-sm font-bold">{c.authorityShort}</div>
              <a href={c.authorityUrl} target="_blank" rel="noopener nofollow" className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-gold">
                เว็บไซต์ทางการ <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-2xl font-bold">ใช้ทำอะไรได้บ้าง?</h2>
            <ul className="mt-5 grid gap-3 md:grid-cols-2">
              {c.useCases.map((u, i) => (
                <li key={i} className="flex items-start gap-2 rounded-xl border border-border bg-card p-4 text-sm">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  <span>{u}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-2xl font-bold">ขั้นตอนการขอจากภูเก็ต</h2>
            <ol className="mt-5 space-y-3">
              {c.steps.map((s, i) => (
                <li key={i} className="flex gap-3 rounded-xl border border-border bg-card p-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold text-sm font-bold text-foreground">
                    {i + 1}
                  </div>
                  <div>
                    <div className="font-bold text-primary">{s.title}</div>
                    <div className="mt-1 text-sm text-muted-foreground">{s.body}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-xl border border-border bg-card p-5">
                <div className="flex items-center gap-2 text-sm font-bold text-primary">
                  <FileText className="h-4 w-4 text-gold" /> ลายนิ้วมือ
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{c.fingerprints}</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-5">
                <div className="flex items-center gap-2 text-sm font-bold text-primary">
                  <Shield className="h-4 w-4 text-gold" /> Apostille / Legalization
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{c.legalization}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-3xl px-4 py-12">
            <h2 className="text-2xl font-bold">คำถามที่พบบ่อย</h2>
            <div className="mt-5 space-y-3">
              {faqs.map((f, i) => (
                <details key={i} className="group rounded-xl border border-border bg-card p-4">
                  <summary className="cursor-pointer font-semibold text-primary">{f.q}</summary>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-xl font-bold">ประเทศอื่นที่นิยมขอตรวจประวัติ</h2>
            <div className="mt-5 grid gap-3 md:grid-cols-3">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to="/police-clearance/$country"
                  params={{ country: r.slug }}
                  className="rounded-xl border border-border bg-card p-4 text-sm transition-all hover:-translate-y-0.5 hover:border-gold"
                >
                  <span className="text-xl">{r.flag}</span>
                  <div className="mt-1 font-bold text-primary">{r.authorityShort}</div>
                  <div className="text-xs text-muted-foreground">{r.countryTh} · เริ่ม ฿{r.priceFrom.toLocaleString()}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto max-w-4xl px-4 py-12 text-center">
            <h2 className="text-2xl font-extrabold">พร้อมขอ {c.authorityShort} แล้วใช่ไหม?</h2>
            <p className="mt-3 text-sm opacity-90">
              ส่งสำเนาพาสปอร์ตและจุดประสงค์การใช้ทางไลน์ ทีมแจ้งราคาและกำหนดส่งภายใน 30 นาที
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <a href={`tel:${COMPANY.phoneIntl}`} className="rounded-md bg-gold px-5 py-2.5 text-sm font-bold text-foreground">
                โทร {COMPANY.phone}
              </a>
              <a href={`https://line.me/R/ti/p/${COMPANY.lineId}`} target="_blank" rel="noopener" className="rounded-md border border-primary-foreground/30 bg-primary-foreground/10 px-5 py-2.5 text-sm font-bold">
                ทักไลน์ {COMPANY.lineId}
              </a>
            </div>
          </div>
        </section>
        <div className="mx-auto max-w-4xl px-4">
          <PageGallery category="background" seed="pc-country" locale="th" />
          <FaqSourceNote topic="police-clearance" locale="th" />
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}